import { IconMenu2, IconMoon, IconSun } from "@tabler/icons-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { use } from "react";
import { MarketingHeaderNav } from "@/components/marketing-header-nav";
import { useAuthOptional } from "@/components/providers/auth";
import {
	ThemeContext,
	type ThemeContextInterface,
} from "@/components/providers/theme-provider";
import { NavUser } from "@/components/sidebar/nav-user";
import { SiteLogo } from "@/components/site-logo";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { config } from "@/data/config";
import { useHeaderScrolled } from "@/hooks/use-header-scrolled";
import { marketingHeaderActionLinkClass } from "@/lib/marketing-nav-styles";
import { isModuleEnabled } from "@/lib/modules";
import { getSiteHeaderChromeClass } from "@/lib/site-header-chrome";
import { cn } from "@/lib/utils";

export default function Header() {
	const { isAuthenticated, user, isInitialLoading } = useAuthOptional();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const { darkTheme, toggleTheme } = use(ThemeContext) as ThemeContextInterface;

	const showGuestAuth = isModuleEnabled("auth");
	const scrolled = useHeaderScrolled();

	if (isInitialLoading) {
		return (
			<header
				className={getSiteHeaderChromeClass(scrolled)}
				aria-busy="true"
				data-auth-loading=""
			>
				<div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-4 sm:h-16 sm:gap-3 sm:px-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:px-8">
					<div className="flex min-w-0 items-center lg:justify-self-start">
						<Link
							to="/"
							aria-label="Homepage"
							className="flex min-w-0 items-center gap-2.5 rounded-full outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							<SiteLogo className="h-7 w-auto shrink-0 sm:h-8" aria-hidden />
							<span className="truncate text-base font-semibold tracking-tight text-foreground sm:text-lg">
								{config.siteName}
							</span>
						</Link>
					</div>
					<div className="hidden lg:block" aria-hidden />
					<div
						className="h-8 w-20 shrink-0 animate-pulse rounded-full bg-muted lg:justify-self-end"
						aria-hidden
					/>
				</div>
			</header>
		);
	}

	return (
		<header
			className={getSiteHeaderChromeClass(scrolled)}
			data-scrolled={scrolled ? "true" : undefined}
		>
			<div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-4 sm:h-16 sm:gap-3 sm:px-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:px-8">
				<div className="flex min-w-0 items-center lg:justify-self-start">
					<Link
						to="/"
						aria-label="Homepage"
						className="flex min-w-0 items-center gap-2.5 rounded-full outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						<SiteLogo className="h-7 w-auto shrink-0 sm:h-8" aria-hidden />
						<span className="truncate text-base font-semibold tracking-tight text-foreground sm:text-lg">
							{config.siteName}
						</span>
					</Link>
				</div>

				<MarketingHeaderNav pathname={pathname} />

				<div className="flex shrink-0 items-center gap-1 sm:gap-2 lg:justify-self-end">
					{!isAuthenticated ? (
						<>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="shrink-0 rounded-full text-muted-foreground"
								onClick={toggleTheme}
								aria-label={`Switch to ${darkTheme ? "light" : "dark"} theme`}
							>
								{darkTheme ? (
									<IconMoon className="size-5" strokeWidth={2} />
								) : (
									<IconSun className="size-5" strokeWidth={2} />
								)}
							</Button>
							{showGuestAuth ? (
								<>
									<Link
										to="/auth/login"
										className={cn(
											marketingHeaderActionLinkClass,
											"hidden lg:inline-flex",
										)}
									>
										Log in
									</Link>
									<Link
										to="/auth/register"
										className={cn(
											buttonVariants({ variant: "outline", size: "sm" }),
											"hidden rounded-full px-4 font-medium lg:inline-flex",
										)}
									>
										Register
									</Link>
								</>
							) : null}
						</>
					) : (
						<NavUser
							user={{
								firstName: user?.firstName ?? "",
								lastName: user?.lastName ?? "",
								email: user?.email ?? "",
								emoji: user?.emoji ?? "👤",
							}}
						/>
					)}

					<div className="lg:hidden">
						<Sheet>
							<SheetTrigger
								type="button"
								className={cn(
									buttonVariants({ variant: "ghost", size: "icon" }),
									"shrink-0 rounded-full text-muted-foreground",
								)}
								aria-label="Open menu"
							>
								<IconMenu2 className="size-5" strokeWidth={2} />
							</SheetTrigger>
							<SheetContent
								side="right"
								className="flex w-[min(100%,20rem)] flex-col gap-0 border-border/40 p-0"
							>
								<SheetHeader className="border-b border-border/40 px-5 py-4 text-left">
									<SheetTitle className="sr-only">
										Primary navigation and account
									</SheetTitle>
									<Link
										to="/"
										className="flex items-center gap-2.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
									>
										<SiteLogo className="h-8 w-auto shrink-0" aria-hidden />
										<span className="truncate text-base font-semibold tracking-tight">
											{config.siteName}
										</span>
									</Link>
								</SheetHeader>
								<div className="flex flex-1 flex-col px-3 py-4">
									<div className="min-h-0 flex-1 overflow-y-auto">
										<p className="mb-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
											Explore
										</p>
										<MarketingHeaderNav pathname={pathname} variant="mobile" />
									</div>
									<div className="mt-auto shrink-0 space-y-5 border-t border-border/40 pt-5">
										<div>
											<p className="mb-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
												Appearance
											</p>
											<div className="px-1">
												<Button
													type="button"
													variant="outline"
													className="h-11 w-full justify-center gap-2 rounded-full text-base font-medium"
													onClick={toggleTheme}
													aria-label={`Set color theme to ${darkTheme ? "light" : "dark"}`}
												>
													{darkTheme ? (
														<IconMoon className="size-5" strokeWidth={2} />
													) : (
														<IconSun className="size-5" strokeWidth={2} />
													)}
													Use {darkTheme ? "light" : "dark"} theme
												</Button>
											</div>
										</div>
										{!isAuthenticated && showGuestAuth ? (
											<div>
												<p className="mb-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
													Account
												</p>
												<div className="flex flex-col gap-2 px-1">
													<Link
														to="/auth/login"
														className={cn(
															buttonVariants({
																variant: "outline",
																size: "default",
															}),
															"h-11 w-full justify-center rounded-full text-base font-medium",
														)}
													>
														Log in
													</Link>
													<Link
														to="/auth/register"
														className={cn(
															buttonVariants({
																variant: "outline",
																size: "default",
															}),
															"h-11 w-full justify-center rounded-full text-base font-medium",
														)}
													>
														Register
													</Link>
												</div>
											</div>
										) : null}
									</div>
								</div>
							</SheetContent>
						</Sheet>
					</div>
				</div>
			</div>
		</header>
	);
}
